import { Fragment, type ReactNode } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'

const OVERSCAN_ROWS = 10

export type VirtualRowProps = {
  ref: (node: HTMLTableRowElement | null) => void
  'data-index': number
  'aria-rowindex': number
}

export function VirtualTableRows<T>({
  items,
  getKey,
  estimateSize,
  scrollElement,
  columnCount,
  renderRow,
}: {
  items: readonly T[]
  getKey: (item: T) => string
  estimateSize: number
  scrollElement: HTMLElement | null
  columnCount: number
  renderRow: (item: T, rowProps: VirtualRowProps) => ReactNode
}) {
  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Virtual manages mutable instance state outside React Compiler.
  const virtualizer = useVirtualizer<HTMLElement, HTMLTableRowElement>({
    count: items.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => estimateSize,
    overscan: OVERSCAN_ROWS,
    getItemKey: (index) => getKey(items[index]!),
  })
  const virtualItems = virtualizer.getVirtualItems()
  const first = virtualItems[0]
  const last = virtualItems[virtualItems.length - 1]
  const topHeight = first?.start ?? 0
  const bottomHeight = last ? virtualizer.getTotalSize() - last.end : 0

  return (
    <>
      {topHeight > 0 ? <SpacerRow height={topHeight} columnCount={columnCount} /> : null}
      {virtualItems.map((virtualItem) => (
        <Fragment key={virtualItem.key}>
          {renderRow(items[virtualItem.index]!, {
            ref: virtualizer.measureElement,
            'data-index': virtualItem.index,
            'aria-rowindex': virtualItem.index + 2,
          })}
        </Fragment>
      ))}
      {bottomHeight > 0 ? <SpacerRow height={bottomHeight} columnCount={columnCount} /> : null}
    </>
  )
}

function SpacerRow({ height, columnCount }: { height: number; columnCount: number }) {
  return (
    <tr aria-hidden="true" data-slot="virtual-row-spacer">
      <td colSpan={columnCount} style={{ height: `${height}px` }} />
    </tr>
  )
}
